import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "./useProject";

/**
 * Variable disponible para usar en fórmulas
 */
export interface FormulaVariable {
  name: string;           // Nombre de la variable (ej: "comunicacion")
  displayName: string;    // Nombre para mostrar (ej: "Comunicación")
  type: 'mapping' | 'calculation';  // Origen de la variable
  sourceType?: string;    // Tipo de source (question, calculation, client, static)
  calculationType?: string; // Tipo de cálculo si es calculation
  description?: string;   // Descripción opcional
  isNumeric: boolean;     // Si es probable que retorne un número
}

/**
 * Cálculo disponible para usar en fórmulas
 */
export interface FormulaCalculation {
  id: number;
  name: string;
  calculationType: string;
  description: string;     // Generado desde config
  isNumeric: boolean;      // Siempre true para aggregate, false para ranking
}

/**
 * Operador matemático disponible
 */
export interface FormulaOperator {
  symbol: string;
  name: string;
  description: string;
}

/**
 * Función matemática disponible
 */
export interface FormulaFunction {
  name: string;
  syntax: string;
  description: string;
  example: string;
}

/**
 * Operadores disponibles para fórmulas
 */
export const FORMULA_OPERATORS: FormulaOperator[] = [
  { symbol: '+', name: 'Suma', description: 'Suma dos valores' },
  { symbol: '-', name: 'Resta', description: 'Resta dos valores' },
  { symbol: '*', name: 'Multiplicación', description: 'Multiplica dos valores' },
  { symbol: '/', name: 'División', description: 'Divide dos valores' },
  { symbol: '(', name: 'Paréntesis Abrir', description: 'Agrupa operaciones' },
  { symbol: ')', name: 'Paréntesis Cerrar', description: 'Cierra grupo de operaciones' },
];

/**
 * Funciones matemáticas disponibles para fórmulas
 */
export const FORMULA_FUNCTIONS: FormulaFunction[] = [
  {
    name: 'round',
    syntax: 'round(valor)',
    description: 'Redondea al entero más cercano',
    example: 'round(4.6) = 5',
  },
  {
    name: 'abs',
    syntax: 'abs(valor)',
    description: 'Valor absoluto',
    example: 'abs(-5) = 5',
  },
  {
    name: 'max',
    syntax: 'max(a, b, ...)',
    description: 'Retorna el valor máximo',
    example: 'max(3, 7, 2) = 7',
  },
  {
    name: 'min',
    syntax: 'min(a, b, ...)',
    description: 'Retorna el valor mínimo',
    example: 'min(3, 7, 2) = 2',
  },
  {
    name: 'avg',
    syntax: 'avg(a, b, ...)',
    description: 'Calcula el promedio',
    example: 'avg(2, 4, 6) = 4',
  },
  {
    name: 'sum',
    syntax: 'sum(a, b, ...)',
    description: 'Suma todos los valores',
    example: 'sum(2, 4, 6) = 12',
  },
  {
    name: 'floor',
    syntax: 'floor(valor)',
    description: 'Redondea hacia abajo',
    example: 'floor(4.9) = 4',
  },
  {
    name: 'ceil',
    syntax: 'ceil(valor)',
    description: 'Redondea hacia arriba',
    example: 'ceil(4.1) = 5',
  },
  {
    name: 'sqrt',
    syntax: 'sqrt(valor)',
    description: 'Raíz cuadrada',
    example: 'sqrt(16) = 4',
  },
  {
    name: 'pow',
    syntax: 'pow(base, exponente)',
    description: 'Potencia',
    example: 'pow(2, 3) = 8',
  },
];

/**
 * Determina si un source_type es probable que retorne un número
 */
function isLikelyNumeric(sourceType: string | null): boolean {
  // Calculations y client created_at pueden ser numéricos
  // Questions pueden ser numéricos o texto - asumimos que podrían ser numéricos
  // Static (current_date) retorna texto
  if (sourceType === 'calculation') return true;
  if (sourceType === 'question') return true; // Asumimos potencialmente numérico
  if (sourceType === 'client') return false;  // nombre, logo, mision son texto
  if (sourceType === 'static') return false;  // Fechas son texto
  return false;
}

/**
 * Genera descripción legible para una configuración de cálculo
 */
function generateCalculationDescription(calculationType: string, config: any): string {
  if (calculationType === 'aggregate') {
    const operation = config?.operation || 'average';
    const scope = config?.scope || 'main';
    const group = config?.group_name;

    let desc = `${operation} en ${scope}`;
    if (group) desc += ` (${group})`;
    return desc;
  }

  if (calculationType === 'ranking') {
    const operation = config?.operation || 'top';
    const limit = config?.limit || 5;
    return `${operation} ${limit} items`;
  }

  if (calculationType === 'formula') {
    return config?.formula || 'Fórmula personalizada';
  }

  return calculationType;
}

/**
 * Hook para obtener variables numéricas disponibles para fórmulas
 *
 * @param templateId - ID del template
 * @returns Variables, cálculos, operadores y funciones disponibles
 */
export function useFormulaVariables(templateId: number | null) {
  const { projectId } = useProject();

  // Query para obtener variable mappings
  const mappingsQuery = useQuery({
    queryKey: ["formula-variables-mappings", templateId, projectId],
    queryFn: async () => {
      if (!templateId || !projectId) {
        return [];
      }

      const { data, error } = await (supabase as any)
        .from("template_variable_mappings")
        .select("variable_name, source_type, calculation_type, description")
        .eq("template_id", templateId)
        .eq("project_id", projectId)
        .order("variable_name");

      if (error) {
        console.error("Error fetching variable mappings for formula:", error);
        throw error;
      }

      return data || [];
    },
    enabled: !!templateId && !!projectId,
  });

  // Query para obtener cálculos (excluyendo fórmulas para evitar dependencias circulares)
  const calculationsQuery = useQuery({
    queryKey: ["formula-variables-calculations", templateId, projectId],
    queryFn: async () => {
      if (!templateId || !projectId) {
        return [];
      }

      const { data, error } = await (supabase as any)
        .from("template_calculations")
        .select("id, name, calculation_type, config")
        .eq("template_id", templateId)
        .eq("project_id", projectId)
        .neq("calculation_type", "formula") // Excluir fórmulas para evitar círculos
        .order("name");

      if (error) {
        console.error("Error fetching calculations for formula:", error);
        throw error;
      }

      return data || [];
    },
    enabled: !!templateId && !!projectId,
  });

  // Transformar mappings a FormulaVariable[]
  const variables: FormulaVariable[] = (mappingsQuery.data || [])
    .filter((mapping: any) => isLikelyNumeric(mapping.source_type))
    .map((mapping: any) => ({
      name: mapping.variable_name,
      displayName: mapping.variable_name
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c: string) => c.toUpperCase()),
      type: 'mapping' as const,
      sourceType: mapping.source_type,
      calculationType: mapping.calculation_type,
      description: mapping.description,
      isNumeric: true,
    }));

  // Transformar calculations a FormulaCalculation[]
  const calculations: FormulaCalculation[] = (calculationsQuery.data || []).map(
    (calc: any) => ({
      id: calc.id,
      name: calc.name,
      calculationType: calc.calculation_type,
      description: generateCalculationDescription(calc.calculation_type, calc.config),
      isNumeric: calc.calculation_type === 'aggregate', // ranking retorna texto
    })
  );

  // Filtrar solo cálculos numéricos
  const numericCalculations = calculations.filter((c) => c.isNumeric);

  return {
    // Variables numéricas de mappings
    variables,

    // Cálculos numéricos disponibles
    calculations: numericCalculations,

    // Operadores matemáticos
    operators: FORMULA_OPERATORS,

    // Funciones matemáticas
    functions: FORMULA_FUNCTIONS,

    // Estados de carga
    isLoading: mappingsQuery.isLoading || calculationsQuery.isLoading,
    error: mappingsQuery.error || calculationsQuery.error,

    // Helpers
    getVariableByName: (name: string) =>
      variables.find((v) => v.name === name),

    getCalculationById: (id: number) =>
      numericCalculations.find((c) => c.id === id),

    // Formato para insertar en fórmula
    formatVariableForFormula: (name: string) => `{${name}}`,
    formatCalculationForFormula: (calcId: number) => {
      const calc = numericCalculations.find((c) => c.id === calcId);
      return calc ? `{calc_${calc.id}}` : '';
    },
  };
}

/**
 * Valida una fórmula
 *
 * @param formula - Fórmula a validar
 * @param availableVariables - Variables disponibles
 * @returns Objeto con resultado de validación
 */
export function validateFormula(
  formula: string,
  availableVariables: string[]
): { isValid: boolean; error?: string; usedVariables: string[] } {
  if (!formula || formula.trim() === '') {
    return { isValid: false, error: 'La fórmula está vacía', usedVariables: [] };
  }

  // Extraer variables usadas en la fórmula
  const variableMatches = formula.match(/\{(\w+)\}/g) || [];
  const usedVariables = variableMatches.map((match) => match.slice(1, -1));

  // Verificar que todas las variables existan
  const unknownVariables = usedVariables.filter(
    (v) => !availableVariables.includes(v) && !v.startsWith('calc_')
  );

  if (unknownVariables.length > 0) {
    return {
      isValid: false,
      error: `Variables desconocidas: ${unknownVariables.join(', ')}`,
      usedVariables,
    };
  }

  // Verificar sintaxis básica (paréntesis balanceados)
  let parenCount = 0;
  for (const char of formula) {
    if (char === '(') parenCount++;
    if (char === ')') parenCount--;
    if (parenCount < 0) {
      return {
        isValid: false,
        error: 'Paréntesis no balanceados',
        usedVariables,
      };
    }
  }
  if (parenCount !== 0) {
    return {
      isValid: false,
      error: 'Paréntesis no balanceados',
      usedVariables,
    };
  }

  // Verificar que no haya operadores consecutivos inválidos
  if (/[+\-*/]{2,}/.test(formula.replace(/\{[^}]+\}/g, 'X'))) {
    return {
      isValid: false,
      error: 'Operadores consecutivos inválidos',
      usedVariables,
    };
  }

  return { isValid: true, usedVariables };
}
